import { AvatarViewModel } from '@/models/AvatarViewModel';
import { OrganisationId } from '@/models/enums/OrganisationId';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { Utils } from '@/services/utils/BasicUtils';

/**
 * Frontend model for the "Opiekun sprawy" (case caretaker) feature.
 *
 * Source of truth is `GET /api/v2/deals/{id}/expert-assignment-history`, which returns a flat,
 * chronological (oldest-first) list of ownership records — one per action (`DEAL_CREATED`, then each
 * `DEAL_DELEGATED`). Every field lives directly on the record (no `from`/`to`/`data` nesting). The
 * newest record is the current caretaker. When the endpoint returns nothing the card falls back to
 * the deal's own fields — see {@link ExpertAssignment.fallback}.
 */

export interface ExpertAssignmentDivision {
  id: number;
  name: string;
}

/** Raw record returned by the expert-assignment-history endpoint (fields are flat on the record). */
export interface ExpertAssignmentHistoryItemDTO {
  userId: number;
  firstName?: Nullable<string>;
  lastName?: Nullable<string>;
  email?: Nullable<string>;
  phone?: Nullable<string>;
  photoUrl?: Nullable<string>;
  organisationId?: OrganisationId;
  /** `DEAL_CREATED` for the original owner, `DEAL_DELEGATED` for each subsequent owner. */
  actionType?: Nullable<string>;
  createdAt?: Nullable<string>;
  branchName?: Nullable<string>;
  branchCity?: Nullable<string>;
  /** Division this owner held for the deal's product at assignment time — only when accredited. */
  divisionId?: Nullable<number>;
  divisionName?: Nullable<string>;
}

/** Structural shape covering both `Agent` and `User` — used to build the deal-field fallback. */
export interface ExpertLike {
  id: number;
  userId?: number;
  name?: Nullable<string>;
  displayName?: Nullable<string>;
  email?: Nullable<string>;
  phone?: Nullable<string>;
  photoUrl?: Nullable<string>;
  organisationId?: OrganisationId;
  branch?: Nullable<{ id: number; name: string }>;
}

/** Source for the fallback timeline derived from the deal's own fields (no delegation-history endpoint). */
export interface ExpertAssignmentFallbackSource {
  agent?: Nullable<ExpertLike>;
  delegatedTo?: Nullable<ExpertLike>;
  isDelegated?: boolean;
}

/**
 * View-model for a single expert in the assignment timeline. Implements {@link AvatarViewModel} so it
 * can be passed straight to `<person-avatar>`.
 */
export class ExpertAssignment implements AvatarViewModel {
  public id!: number;
  public name: string = '';
  public email?: string;
  public phone?: string;
  public photoUrl?: Nullable<string>;
  public organisationId?: OrganisationId;
  public branchName?: Nullable<string> = null;
  public branchCity?: Nullable<string> = null;
  /** Division resolved for this assignment (snapshot from the history record for historical entries). */
  public division: Nullable<ExpertAssignmentDivision> = null;
  /** When this expert became the caretaker (record `createdAt`). */
  public assignedAt: Nullable<string> = null;
  /** Action that put this expert on the deal (`DEAL_CREATED` / `DEAL_DELEGATED`). */
  public actionType: Nullable<string> = null;
  /** True for the expert currently responsible for the deal — highlighted in the UI. */
  public isCurrent: boolean = false;
  /**
   * True for the first "real" expert that processed the deal. Per AC, a deal created/delegated by the
   * CC ("opiekun zerowy") is never the first expert — the backend omits that record, so the oldest
   * record we receive is already the first real expert. Used by the settlement summary (AC#4/#5).
   */
  public isFirst: boolean = false;

  constructor(dto?: Partial<ExpertAssignment>) {
    if (dto) {
      Utils.assign(this, dto);
    }
  }

  get displayName(): string {
    return this.name || '-';
  }

  get initials(): string {
    return this.name
      ? this.name.trim().split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase()
      : '?';
  }

  get organisationName(): Nullable<string> {
    return this.organisationId != null ? I18NGetter().OrganisationId[this.organisationId] : null;
  }

  /**
   * Placówka label. On prod the branch name already contains the city, so we show only the name to
   * avoid duplication (e.g. "Łódź – Żeromskiego" instead of "Łódź – Żeromskiego, Łódź").
   */
  get branchLabel(): Nullable<string> {
    return this.branchName ?? null;
  }

  /** Has enough data to render the second ("Org • email") line of a row. */
  get hasSubtitle(): boolean {
    return Boolean(this.organisationName || this.email);
  }

  private static fullName(firstName?: Nullable<string>, lastName?: Nullable<string>): string {
    return [firstName, lastName].map(part => (part ?? '').trim()).filter(Boolean).join(' ');
  }

  private static itemToAssignment(item: ExpertAssignmentHistoryItemDTO): ExpertAssignment {
    const division: Nullable<ExpertAssignmentDivision> =
      item.divisionId != null && item.divisionName
        ? { id: item.divisionId, name: item.divisionName }
        : null;
    return new ExpertAssignment({
      id: item.userId,
      name: ExpertAssignment.fullName(item.firstName, item.lastName),
      email: item.email ?? undefined,
      phone: item.phone ?? undefined,
      photoUrl: item.photoUrl ?? null,
      organisationId: item.organisationId,
      branchName: item.branchName ?? null,
      branchCity: item.branchCity ?? null,
      division,
      assignedAt: item.createdAt ?? null,
      actionType: item.actionType ?? null,
    });
  }

  /**
   * Builds the ordered caretaker timeline from the raw history. The endpoint returns one record per
   * ownership action (`DEAL_CREATED` first, then each `DEAL_DELEGATED`); ordered by `createdAt` the
   * newest record is the current caretaker. Returns newest-first with the current entry flagged.
   */
  public static fromHistory(history: ExpertAssignmentHistoryItemDTO[] = []): ExpertAssignment[] {
    const chronological = (history ?? [])
      .filter(item => item && item.userId != null)
      .slice()
      .sort((a, b) => new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime())
      .map(item => ExpertAssignment.itemToAssignment(item));

    if (chronological.length) {
      chronological[0].isFirst = true;
      chronological[chronological.length - 1].isCurrent = true;
    }

    return chronological.reverse();
  }

  /** First real expert in the timeline (oldest record). See {@link isFirst}. */
  public static firstExpert(experts: ExpertAssignment[]): Nullable<ExpertAssignment> {
    return experts.find(expert => expert.isFirst) ?? experts[experts.length - 1] ?? null;
  }

  /** Currently-responsible (last) expert in the timeline (newest record). See {@link isCurrent}. */
  public static lastExpert(experts: ExpertAssignment[]): Nullable<ExpertAssignment> {
    return experts.find(expert => expert.isCurrent) ?? experts[0] ?? null;
  }

  /** Maps an Agent/User-like person into an entry. Division is unknown without a history record. */
  public static fromPerson(person: ExpertLike, isCurrent: boolean): ExpertAssignment {
    return new ExpertAssignment({
      id: person.userId ?? person.id,
      name: (person.name && person.name.trim()) || person.displayName || '',
      email: person.email ?? undefined,
      phone: person.phone ?? undefined,
      photoUrl: person.photoUrl,
      organisationId: person.organisationId,
      branchName: person.branch?.name ?? null,
      division: null,
      isCurrent,
    });
  }

  /**
   * Fallback timeline built from the deal's own fields, used when the delegation-history endpoint
   * returns nothing (never-delegated deal, 404, or not yet populated):
   *  - delegated deal → current = `delegatedTo`, previous = `agent` (last hop only);
   *  - otherwise      → current = `agent`.
   * Once the endpoint returns records, {@link fromHistory} takes precedence and this is unused.
   */
  public static fallback(source: ExpertAssignmentFallbackSource): ExpertAssignment[] {
    const { agent, delegatedTo, isDelegated } = source;
    const idOf = (p: ExpertLike) => p.userId ?? p.id;

    let result: ExpertAssignment[];
    if (isDelegated && delegatedTo) {
      result = [ExpertAssignment.fromPerson(delegatedTo, true)];
      if (agent && idOf(agent) !== idOf(delegatedTo)) {
        result.push(ExpertAssignment.fromPerson(agent, false));
      }
    } else {
      result = agent ? [ExpertAssignment.fromPerson(agent, true)] : [];
    }
    // Oldest entry (last in this newest-first list) is the first expert.
    if (result.length) {
      result[result.length - 1].isFirst = true;
    }
    return result;
  }
}
